export type ProgramChapterKind = "Start" | "Core" | "Deep dive" | "Platform path" | "Advanced" | "Governance" | "Evidence";

export interface ProgramChapter {
  number: string;
  kind: ProgramChapterKind;
  title: string;
  outcome: string;
  href: string;
}

export const LONGHAND_PROGRAM_ATLAS: ProgramChapter[] = [
  {
    number: "00",
    kind: "Start",
    title: "Prepare the workspace",
    outcome: "An approved Claude or ChatGPT workspace with browser access, one connector, Sea of Demand, and a bounded Chief of Staff test.",
    href: "/ai-30/program/prep",
  },
  {
    number: "01",
    kind: "Core",
    title: "Complex Prompting Made Easy",
    outcome: "A reusable Chief of Staff Skill with a visible reasoning method.",
    href: "#session-1",
  },
  {
    number: "01A",
    kind: "Deep dive",
    title: "From source to finished artifact",
    outcome: "A traceable work product created from an approved file.",
    href: "#source-grounded-artifact-lab",
  },
  {
    number: "02",
    kind: "Core",
    title: "Pick the Right Work for AI",
    outcome: "A defensible Job selected from real recurring work.",
    href: "#session-2",
  },
  {
    number: "02A",
    kind: "Deep dive",
    title: "Decision lab: RICE → TDAR → DMP",
    outcome: "A ranked opportunity, measurable result, and reviewable decision record.",
    href: "#decision-lab",
  },
  {
    number: "03",
    kind: "Core",
    title: "Pick the Best AI Tool for the Job",
    outcome: "A tool decision tied to the work, evidence, access, and risk.",
    href: "#session-3",
  },
  {
    number: "03A",
    kind: "Platform path",
    title: "Configure the tools you actually use",
    outcome: "A platform-specific setup with tested permissions and fallbacks.",
    href: "#platform-paths",
  },
  {
    number: "04",
    kind: "Core",
    title: "Control AI Agents",
    outcome: "A reviewed agent run with bounded access and a human stop rule.",
    href: "#session-4",
  },
  {
    number: "04A",
    kind: "Advanced",
    title: "Safe builds and agent handoffs",
    outcome: "A reviewable build or two-agent handoff with evidence at every boundary.",
    href: "#advanced-agent-work",
  },
  {
    number: "05",
    kind: "Core",
    title: "Improve AI Agents",
    outcome: "A measurable loop that changes the next run.",
    href: "#session-5",
  },
  {
    number: "05A",
    kind: "Deep dive",
    title: "Package and share the Skill",
    outcome: "A tested, versioned, portable Skill another person can use.",
    href: "#skill-forge-lab",
  },
  {
    number: "06",
    kind: "Governance",
    title: "Safety & Governance",
    outcome: "A documented boundary, impact review, owner, and escalation path.",
    href: "#safety-governance",
  },
  {
    number: "07",
    kind: "Evidence",
    title: "Program evidence portfolio",
    outcome: "Four connected work products showing what you can repeat and improve.",
    href: "#evidence-portfolio",
  },
];

export interface PlatformPath {
  id: string;
  label: string;
  title: string;
  description: string;
  useWhen: string;
  sequence: string[];
  links: { label: string; href: string }[];
  stopRule: string;
}

export const LONGHAND_PLATFORM_PATHS: PlatformPath[] = [
  {
    id: "claude-path",
    label: "Claude",
    title: "Chat → Project → Browser or Cowork → Code",
    description: "Move up one surface only when the Job needs more context, access, or action than the current surface can safely provide.",
    useWhen: "Your approved work can live in Claude and you need a path from conversation to reusable, agentic work.",
    sequence: [
      "Use Chat to frame and test the request.",
      "Use a Project to hold approved instructions, sources, and examples.",
      "Use Browser or Cowork when the Job needs supervised access to web or desktop work.",
      "Use Claude Code for complex file or code work in an isolated, recoverable workspace.",
    ],
    links: [
      { label: "Claude fundamentals", href: "/claude-fundamentals" },
      { label: "Claude masterclass", href: "/claude-masterclass" },
      { label: "Agent starter setup", href: "/ai-agent-starter" },
    ],
    stopRule: "Do not move to a more agentic surface just because it is available. Require a Job-level reason and a tested permission boundary.",
  },
  {
    id: "microsoft-path",
    label: "Microsoft 365",
    title: "Copilot Chat → Microsoft 365 sources → Cowork",
    description: "Keep the same Job, evidence, and review logic while working inside the Microsoft tools your organization already governs.",
    useWhen: "The authorized evidence and collaboration already live in Microsoft 365.",
    sequence: [
      "Confirm which work account and tenant contain the approved sources.",
      "Test the smallest read-only task in Copilot Chat.",
      "Add only the Microsoft 365 sources the Job needs.",
      "Use Cowork for a supervised multi-step workflow and inspect every proposed action.",
    ],
    links: [{ label: "Copilot Cowork guide", href: "/copilot-cowork" }],
    stopRule: "A source being visible in Microsoft 365 does not automatically make it appropriate for the AI workflow. Verify purpose and authorization.",
  },
  {
    id: "builder-path",
    label: "Builder",
    title: "Job brief → sandbox → reviewed prototype → handoff",
    description: "Use the builder path when the selected Job needs a small tool, workflow, or code-backed prototype rather than another prompt.",
    useWhen: "The Job has an observable finish line and a bounded prototype would test the idea faster than more discussion.",
    sequence: [
      "Frame the user, trigger, inputs, outputs, happy path, and boundaries.",
      "Create an isolated, recoverable workspace and acceptance tests.",
      "Build one coherent slice at a time and inspect the behavior and the change.",
      "Package the evidence so Product, Engineering, Security, or another owner can decide what happens next.",
    ],
    links: [{ label: "AI Builder Playbook", href: "/ai-builder-playbook" }],
    stopRule: "A working prototype is evidence for a decision. It is not production approval.",
  },
];

export interface AgentHandoffStep {
  title: string;
  action: string;
  evidence: string;
}

export const TWO_AGENT_HANDOFF_LAB: AgentHandoffStep[] = [
  {
    title: "Assign non-overlapping roles",
    action: "Give Agent 1 one bounded production or research task. Give Agent 2 a separate verification task with explicit criteria. Name the human owner who resolves disagreement.",
    evidence: "A role card for each agent showing its inputs, allowed tools, output, and prohibited actions.",
  },
  {
    title: "Write the handoff contract",
    action: "Specify the artifact Agent 1 must leave, what provenance travels with it, what Agent 2 must check, and the exact conditions that stop the handoff.",
    evidence: "A handoff record with artifact location, source list, acceptance checks, and stop conditions.",
  },
  {
    title: "Run one low-risk handoff",
    action: "Use synthetic or approved data. Let Agent 1 complete only its slice, then let Agent 2 inspect the result without silently fixing it.",
    evidence: "The original output, independent review, discrepancies, and any intervention made by the human owner.",
  },
  {
    title: "Reconcile before continuing",
    action: "Have the human owner accept, revise, or reject the work. Change the role, contract, or check before another run if the failure could repeat.",
    evidence: "A dated decision and one documented change to the next run.",
  },
];

export interface EvidenceMilestone {
  id: string;
  number: string;
  title: string;
  proves: string;
  evidence: string[];
  reviewQuestion: string;
}

export const LONGHAND_EVIDENCE_MILESTONES: EvidenceMilestone[] = [
  {
    id: "opportunity-diagnosis",
    number: "01",
    title: "Opportunity Diagnosis",
    proves: "You can choose valuable work before choosing technology.",
    evidence: [
      "A real Job-to-be-Done and candidate workflow inventory",
      "A RICE ranking with stated evidence and uncertainty",
      "A TDAR brief with baseline, human contribution, and remeasurement plan",
      "A DMP record showing the strongest alternative and the build, defer, or reject decision",
    ],
    reviewQuestion: "Would another leader understand why this Job deserves the next experiment?",
  },
  {
    id: "source-grounded-assistant",
    number: "02",
    title: "Source-Grounded Assistant",
    proves: "You can create useful work from authorized evidence without hiding uncertainty.",
    evidence: [
      "The approved source set and its access boundary",
      "A reusable Skill or Project instruction set",
      "A finished artifact with acceptance criteria",
      "A claim, source, and correction log from human review",
    ],
    reviewQuestion: "Can a reviewer trace the important claims and reproduce the result from the same sources?",
  },
  {
    id: "bounded-agent",
    number: "03",
    title: "Bounded Agent or Loop",
    proves: "You can control access, action, execution, review, and failure handling.",
    evidence: [
      "An operating brief with outcome, steps, tools, and Definition of Done",
      "An access-and-action ladder with approvals and stop rules",
      "The original agent output, intervention log, and verification result",
      "A two-agent handoff or bounded loop test with exception handling",
    ],
    reviewQuestion: "Could the run fail safely, and can a person see exactly what happened?",
  },
  {
    id: "ai-work-capstone",
    number: "04",
    title: "AI Work Capstone",
    proves: "You can transfer and improve the whole system against a real result.",
    evidence: [
      "The same work sample and rubric captured before and after the agent",
      "A versioned, packaged Skill with test cases and a handoff record",
      "A metric loop with owner, source, cadence, threshold, and exception route",
      "A human decision explaining what changed, what did not, and what the next run will test",
    ],
    reviewQuestion: "Does the evidence show a repeatable capability and a measurable improvement, not just a polished demo?",
  },
];
